
import { Vue, Component, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import QrcodeReader from '@/components/shared/QrcodeReader.vue';
import TextInputButton from '@/components/shared/TextInputButton.vue';
import Tooltip from '@/components/shared/Tooltip.vue';
import '@/validation-rules';

const namespace: string = 'cart';

@Component<CartVoucher>({
	components: {
		QrcodeReader,
		TextInputButton,
		Tooltip
	}
})

export default class CartVoucher extends Vue {
	@Prop({ type: Object, required: true, default: () => {} }) private restaurantVoucherInfo!: Voucher;
	@Action('validateVoucherCodeAndGetValue', { namespace }) private validateVoucherCodeAndGetValue!: (object: any) => void;
	@Action('removeVoucherCode', { namespace }) private removeVoucherCode!: () => void;
	@Action('toggleSaveVoucher', { namespace }) private setSaveVoucher!: (save: boolean) => void;
	@Getter('getVoucherInfo', { namespace }) private voucherInfo!: any;
	@Getter('getSaveVoucher', { namespace }) private saveVoucher!: boolean;
	@Getter('getUserVoucher', { namespace: 'auth' }) private savedUserVoucher!: UserVoucher | null;
	@Getter('isAuthenticated', { namespace: 'auth' }) private isAuthenticated!: boolean;
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;

	$refs!: { qrcodeButton: HTMLButtonElement };

	private voucherCode: string = '';
	private voucherMessage: string = '';
	private validatingCode: boolean = false;
	private showQrcodeReader: boolean = false;
	private qrCodeLoading: boolean = false;
	private tooltipOpened: boolean = false;

	private get activeVoucherCode(): string {
		return this.voucherCode || this.voucherInfo?.code || this.savedUserVoucher?.code || '';
	}

	/**
	 * Get voucher code if it was applied before or prefill from saved profile
	 *
	 * @return {void}
	 */
	private created(): void {
		if (this.activeVoucherCode) {
			this.voucherCode = this.activeVoucherCode;

			// Set success state if voucher was applied before
			if (this.voucherInfo?.code) {
				this.voucherMessage = this.$t('checkout.cart.voucher.code_applied');
			}
		}

		// If the prefilled gift card is the saved gift card set save voucher to true
		if (this.voucherCode === this.savedUserVoucher?.code) {
			this.setSaveVoucher(true);
		}
	}

	private mounted() {
		this.positionQrcodeButton();
	}

	/**
	 * Position QR code button based on button width
	 *
	 * @return {void}
	 */
	private positionQrcodeButton(): void {
		if (!this.$refs.qrcodeButton) {
			return;
		}
		const inputButton = this.$el.querySelector('.input-button') as HTMLElement;
		if (inputButton) {
			const buttonWidthRem = inputButton.offsetWidth / parseFloat(getComputedStyle(document.documentElement).fontSize);
			this.$refs.qrcodeButton.style.right = `${buttonWidthRem + 1.25}rem`;
		}
	}

	/**
	 * Decode qr code
	 *
	 * @return {void}
	 */
	private async decodeQrcode(code: string): Promise<void> {
		Vue.set(this, 'voucherCode', code);
		await this.$nextTick().then(() => {
			this.applyVoucher();
		});
	}

	/**
	 * Validate and apply voucher to the subtotal
	 *
	 * @return {void}
	 */
	private async applyVoucher(): Promise<void> {
		this.voucherMessage = '';
		try {
			this.validatingCode = true;
			await this.validateVoucherCodeAndGetValue(this.voucherCode);
			this.voucherMessage = this.$t('checkout.cart.voucher.code_applied');
		}
		catch (error) {
			this.voucherCode = '';
			if(typeof error === 'string') {
				this.voucherMessage = error;
			}
			else {
				this.voucherMessage = this.$t('checkout.cart.voucher.code_failed');
			}
		}
		finally {
			this.validatingCode = false;
			this.voucherCode = this.voucherInfo.code;
			this.showQrcodeReader = false;
		}
	}

	/**
	 * Scroll to the bottom when opening a selector
	 *
	 * @return {void}
	 */
	private openCloseQrcodeReader(): void {
		this.showQrcodeReader = !this.showQrcodeReader;

		if (this.showQrcodeReader) {
			setTimeout(() => {
				var contentDiv = document.getElementById('cart-content');
				contentDiv!.scrollTop = 500;
			}, 100);
		}
	}

	/**
	 * Remove the applied voucher and reset save state
	 *
	 * @return {Promise<void>}
	 */
	private async removeVoucher(): Promise<void> {
		try {
			this.validatingCode = true;
			await this.removeVoucherCode();
			this.voucherMessage = '';
			this.voucherCode = '';
			this.setSaveVoucher(false);
		}
		catch {
			this.voucherMessage = this.$t('checkout.cart.voucher.code_failed');
		}
		finally {
			setTimeout(() => {
				this.validatingCode = false;
			}, 500);
		}
	}
}
